import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Maximize2 } from "lucide-react";
import { PROJECTS } from "../lib/constants";
import { Compare } from "./BeforeAfter";

function ProjectModal({ project, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <motion.div
      className="fixed inset-0 z-[110] overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="project-modal"
    >
      <div className="absolute inset-0 bg-midnight/85 backdrop-blur-lg" onClick={onClose} data-testid="project-modal-backdrop" />
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <motion.div
          initial={{ y: 50, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 40, opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="sticky top-0 z-10 flex items-center justify-between gap-4 bg-midnight/90 backdrop-blur-xl border border-white/10 rounded-2xl px-5 sm:px-7 py-4 mb-6">
            <div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-crystal">
                {project.items.length} photos — the whole place
              </div>
              <h3 className="font-display text-2xl sm:text-3xl text-white leading-tight mt-1" data-testid="project-modal-title">
                {project.title}
              </h3>
            </div>
            <button
              data-testid="project-modal-close"
              onClick={onClose}
              className="shrink-0 rounded-full border border-white/15 p-3 text-mist hover:text-white hover:border-crystal transition-colors duration-200"
              aria-label="Close project gallery"
            >
              <X size={18} />
            </button>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 pb-16">
            {project.items.map((item, i) => (
              <motion.figure
                key={i}
                initial={{ opacity: 0, y: 28 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.07, ease: [0.22, 1, 0.36, 1] }}
                className={item.type === "ba" ? "sm:col-span-2" : ""}
              >
                {item.type === "ba" ? (
                  <div className="rounded-3xl overflow-hidden border border-crystal/30">
                    <Compare pair={item} index={i} />
                  </div>
                ) : (
                  <div
                    className="relative w-full aspect-[4/3] overflow-hidden rounded-3xl border border-white/10"
                    style={{ boxShadow: "0 30px 70px -35px rgba(0,0,0,0.9)" }}
                    data-testid={`project-photo-${project.key}-${i}`}
                  >
                    <img src={item.src} alt={item.caption} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-midnight/80 via-transparent to-transparent" />
                  </div>
                )}
                {item.type !== "ba" && (
                  <figcaption className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-mist">
                    {item.caption}
                  </figcaption>
                )}
              </motion.figure>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
}

export default function Gallery() {
  const [activeKey, setActiveKey] = useState(null);
  const active = PROJECTS.find((p) => p.key === activeKey) || null;

  const spans = ["lg:col-span-7", "lg:col-span-5", "lg:col-span-12"];
  const heights = ["h-[420px]", "h-[420px]", "h-[400px]"];

  return (
    <section id="work" className="relative py-24 sm:py-32 bg-ink/40 border-y border-white/5">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7 }}
          className="flex flex-wrap items-end justify-between gap-6 mb-12"
        >
          <div>
            <span className="block font-display italic text-7xl sm:text-8xl leading-none text-outline select-none mb-4" aria-hidden="true">02</span>
            <div className="flex items-center gap-3 mb-5">
              <span className="w-9 h-[1px] bg-crystal" />
              <span className="font-mono text-[11px] tracking-[0.35em] uppercase text-crystal">The Work</span>
            </div>
            <h2 className="font-display font-medium text-[clamp(2.2rem,5vw,4.2rem)] leading-[1.02] text-white tracking-tight">
              Proof you can <em className="italic text-crystal">see through.</em>
            </h2>
          </div>
          <p className="max-w-md text-mist text-sm leading-relaxed">
            Tap any place to walk the whole job — every window, and a
            before/after you can drag.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6">
          {PROJECTS.map((p, i) => (
            <motion.button
              key={p.key}
              data-testid={`gallery-card-${p.key}`}
              onClick={() => setActiveKey(p.key)}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, delay: (i % 2) * 0.08, ease: [0.22, 1, 0.36, 1] }}
              className={`${spans[i]} group text-left cursor-pointer`}
            >
              <div
                className={`relative ${heights[i]} w-full overflow-hidden rounded-3xl border border-white/10 transition-all duration-200 group-hover:-translate-y-1 group-hover:border-crystal/50`}
                style={{ boxShadow: "0 40px 90px -40px rgba(0,0,0,0.9)" }}
              >
                <img
                  src={p.cover}
                  alt={p.cardCaption}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-midnight/90 via-transparent to-transparent" />
                <span className="absolute top-5 right-5 rounded-full glass-panel px-4 py-2 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-crystal opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <Maximize2 size={12} /> View the whole place
                </span>
                <figcaption className="absolute bottom-5 left-6 right-6 flex items-end justify-between gap-4">
                  <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/85">{p.cardCaption}</span>
                  <span className="hidden sm:block w-10 h-[1px] bg-crystal shrink-0 mb-1" />
                </figcaption>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && <ProjectModal project={active} onClose={() => setActiveKey(null)} />}
      </AnimatePresence>
    </section>
  );
}
